<template>
  <div class="min-h-screen bg-off-black text-white">
    <div class="container mx-auto px-4 py-16">
      <!-- Header Section -->
      <div class="text-center mb-16">
        <h1 class="text-5xl md:text-6xl font-serif font-semibold mb-6 text-vibe-green">
          Resources
        </h1>
        <p class="text-xl md:text-2xl text-gray-green max-w-3xl mx-auto">
          Free guides and checklists to help legal and accounting professionals navigate compliance, onboarding, and risk management.
        </p>
      </div>

      <!-- Resources Grid -->
      <div class="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">

        <!-- Beneficial Ownership Verification Toolkit Card -->
        <div class="bg-green-700 rounded-lg overflow-hidden shadow-2xl border border-green-600 hover:border-vibe-green transition-all duration-300 transform hover:scale-105">
          <div class="p-8">
            <div class="flex items-start justify-between mb-4">
              <span class="inline-block px-3 py-1 text-xs font-semibold bg-vibe-green text-off-black rounded-full">
                NEW
              </span>
              <span class="text-4xl">📋</span>
            </div>
            <h2 class="text-2xl font-serif font-semibold mb-3 text-vibe-green">
              2026 Beneficial Ownership Verification Toolkit
            </h2>
            <p class="text-gray-green mb-6">
              A practical toolkit to help solicitors and accountants navigate the 2026 beneficial ownership reforms with clear, actionable guidance.
            </p>
            <div class="mb-6">
              <h3 class="text-sm font-semibold text-vibe-green mb-2">What's Inside:</h3>
              <ul class="text-sm text-gray-green space-y-1">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Identify & verify beneficial owners</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Assess control, influence & economic reality</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Challenge inconsistencies and document reasoning</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Structured templates for BO, overseas entities & trusts</span>
                </li>
              </ul>
            </div>
            <router-link
              to="/bo-verification-toolkit"
              class="btn-primary inline-block text-center w-full py-3 transition-all duration-300 hover:bg-swamp-green hover:text-white"
            >
              Download Free Toolkit
            </router-link>
          </div>
        </div>

        <!-- CDD Compliance Checklist Card -->
        <div class="bg-green-700 rounded-lg overflow-hidden shadow-2xl border border-green-600 hover:border-vibe-green transition-all duration-300 transform hover:scale-105">
          <div class="p-8">
            <div class="flex items-start justify-between mb-4">
              <span class="inline-block px-3 py-1 text-xs font-semibold bg-green-600 text-white rounded-full">
                ESSENTIAL
              </span>
              <span class="text-4xl">✅</span>
            </div>
            <h2 class="text-2xl font-serif font-semibold mb-3 text-vibe-green">
              2026 CDD Compliance Checklist
            </h2>
            <p class="text-gray-green mb-6">
              Comprehensive checklist for solicitors and accountants implementing the new 2026 CDD standards with clear, actionable steps.
            </p>
            <div class="mb-6">
              <h3 class="text-sm font-semibold text-vibe-green mb-2">What's Inside:</h3>
              <ul class="text-sm text-gray-green space-y-1">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Client Identification & Verification</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Beneficial Ownership (BO) Checks</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Enhanced Due Diligence (EDD) triggers</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Source of Funds & Source of Wealth</span>
                </li>
              </ul>
            </div>
            <router-link
              to="/cdd-compliance-checklist"
              class="btn-primary inline-block text-center w-full py-3 transition-all duration-300 hover:bg-swamp-green hover:text-white"
            >
              Download Free Checklist
            </router-link>
          </div>
        </div>

        <!-- Smart Onboarding Guide Card -->
        <div class="bg-green-700 rounded-lg overflow-hidden shadow-2xl border border-green-600 hover:border-vibe-green transition-all duration-300 transform hover:scale-105">
          <div class="p-8">
            <div class="flex items-start justify-between mb-4">
              <span class="inline-block px-3 py-1 text-xs font-semibold bg-green-600 text-white rounded-full">
                POPULAR
              </span>
              <span class="text-4xl">📘</span>
            </div>
            <h2 class="text-2xl font-serif font-semibold mb-3 text-vibe-green">
              Smart Onboarding Guide
            </h2>
            <p class="text-gray-green mb-6">
              A practical guide to AML compliance, risk management, and onboarding efficiency for legal and accounting professionals.
            </p>
            <div class="mb-6">
              <h3 class="text-sm font-semibold text-vibe-green mb-2">What's Inside:</h3>
              <ul class="text-sm text-gray-green space-y-1">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Risk-based onboarding strategies</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Sector-specific insights & best practices</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>OSINT & HUMINT intelligence gathering</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>ECCTA reforms & compliance guidance</span>
                </li>
              </ul>
            </div>
            <router-link
              to="/smart-onboarding-download"
              class="btn-primary inline-block text-center w-full py-3 transition-all duration-300 hover:bg-swamp-green hover:text-white"
            >
              Download Free Guide
            </router-link>
          </div>
        </div>

        <!-- Understanding Professional Enablement Card -->
        <div class="bg-green-700 rounded-lg overflow-hidden shadow-2xl border border-green-600 hover:border-vibe-green transition-all duration-300 transform hover:scale-105">
          <div class="p-8">
            <div class="flex items-start justify-between mb-4">
              <span class="inline-block px-3 py-1 text-xs font-semibold bg-vibe-green text-off-black rounded-full">
                NEW
              </span>
              <span class="text-4xl">🛡️</span>
            </div>
            <h2 class="text-2xl font-serif font-semibold mb-3 text-vibe-green">
              Understanding Professional Enablement
            </h2>
            <p class="text-gray-green mb-6">
              A staff information sheet to help professional services firms recognise and prevent enablement risks.
            </p>
            <div class="mb-6">
              <h3 class="text-sm font-semibold text-vibe-green mb-2">What's Inside:</h3>
              <ul class="text-sm text-gray-green space-y-1">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>What enablement means & why it matters</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Three forms: deliberate, wilful blindness, accidental</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Real-world scenarios in practice</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">•</span>
                  <span>Self-assessment questions to counter risk</span>
                </li>
              </ul>
            </div>
            <router-link
              to="/professional-enablement"
              class="btn-primary inline-block text-center w-full py-3 transition-all duration-300 hover:bg-swamp-green hover:text-white"
            >
              Download Free Info Sheet
            </router-link>
          </div>
        </div>

      </div>

      <!-- CTA Section -->
      <div class="mt-16 max-w-4xl mx-auto bg-green-800 rounded-lg p-8 md:p-12 border border-green-600 text-center">
        <h2 class="text-3xl font-serif font-semibold mb-4 text-vibe-green">
          Ready to Automate Your Processes?
        </h2>
        <p class="text-xl text-gray-green mb-8">
          The Marker AI platform takes these principles and automates them, helping you streamline workflows, strengthen compliance, and support commercial momentum.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link
            to="/the-platform"
            class="btn-primary inline-block px-8 py-3 transition-all duration-300 hover:bg-swamp-green hover:text-white"
          >
            Explore Our Platform
          </router-link>
          <router-link
            to="/contact-us"
            class="bg-green-700 hover:bg-green-600 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300 border border-green-600"
          >
            Get in Touch
          </router-link>
        </div>
      </div>

      <!-- Future Resources Note -->
      <div class="mt-12 text-center text-gray-green text-sm">
        <p>New resources added monthly. Check back soon for more compliance guides and industry insights.</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResourcesPage',
  metaInfo: {
    title: 'Resources - Marker AI',
    meta: [
      {
        name: 'description',
        content: 'Free compliance guides, toolkits and resources for legal and accounting professionals. Download our 2026 Beneficial Ownership Verification Toolkit, CDD Compliance Checklist and Smart Onboarding Guide.'
      }
    ]
  }
};
</script>

<style scoped>
/* Additional component-specific styles if needed */
</style>
